import { watch } from 'vue'
import { useGatewayStore } from '@/ContextTab/stores/gateway'
import { AuthStatus, useAuthStore } from '@/ContextTab/stores/auth/auth'
import { TabInitStatus, useInitStore } from '@/ContextTab/stores/init'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile'
import { useNewsStore } from '@/ContextTab/stores/news'
import { usePublishedNewsStore } from '@/ContextTab/stores/newsSet/published/published'
import { useMyNewsStore } from '@/ContextTab/stores/newsSet/task-panel/my'
import { useWaitingNewsStore } from '@/ContextTab/stores/newsSet/task-panel/waiting'
import { useInProgressNewsStore } from '@/ContextTab/stores/newsSet/task-panel/inProgress'
import { GatewaySubType } from '@/Workers/Gateway/gwService/constants'
import { taskPanelMessageHandler } from '@/ContextTab/stores/newsSet/task-panel/messageHandler'
import { gwBridge } from '@/ContextTab/services/gwBridge'
import { trueSleep } from '@/utils/timers'
import { tabMetrics } from '@/ContextTab/services/tabMetrics'


const WORKER_CONNECTION_ATTEMPTS = 60
const WORKER_CONNECTION_INTERVAL = 1000
const WORKER_PING_INTERVAL = 15_000

export function useGatewayWatcher() {

  const gatewayStore = useGatewayStore()
  const userProfileStore = useUserProfileStore()
  const newsStoreStore = useNewsStore()
  const publishedNewsStore = usePublishedNewsStore()
  const myNewsStore = useMyNewsStore()
  const waitingNewsStore = useWaitingNewsStore()
  const inProgressNewsStore = useInProgressNewsStore()
  const authStore = useAuthStore()
  const initStore = useInitStore()


  function subscribeStores() {
    const noStoreSubscriptions: GatewaySubType[] = [
      // subs to pass to legacy without stores
      GatewaySubType.USER_NEWSBREAKS,
      GatewaySubType.USER_NOTIFICATIONS,
      GatewaySubType.USER_ASOI_NOTES,
      GatewaySubType.ASOI_NOTES,
    ]

    noStoreSubscriptions.forEach((subType) => gatewayStore.subscribe(subType, {}))

    gatewayStore.subscribe(GatewaySubType.USER_PROFILE, {}, userProfileStore.onGwUserProfileUpdate)

    gatewayStore.subscribe(GatewaySubType.NEWS, {}, newsStoreStore.onGwNewsMessage)
    gatewayStore.subscribe(GatewaySubType.USER_NEWS, {}, newsStoreStore.onGwNewsMessage)

    gatewayStore.subscribe(GatewaySubType.NEWS, {}, publishedNewsStore.onGwNewsMessage)

    const tpMessageHandler = taskPanelMessageHandler([
      myNewsStore,
      waitingNewsStore,
      inProgressNewsStore,
    ])

    gatewayStore.subscribe(GatewaySubType.NEWS, {}, tpMessageHandler)
    gatewayStore.subscribe(GatewaySubType.USER_NEWS, {}, tpMessageHandler)
  }

  async function tryConnectWorker() {
    if (gatewayStore.isWorkerLoaded) {
      return true // worker already loaded
    }

    let attempt = 1
    while (attempt < WORKER_CONNECTION_ATTEMPTS) {
      console.log(`Attempt ${attempt} to connect worker...`)
      // if worker not loaded, big timeout wouldn't help as bridge instance not exists
      //  -> have to send requests until loaded
      const gwwResponse = await gwBridge.sendMessageWithResult('checkReady', null, { timeout: 1000 })

      if (
        (
          gwwResponse.result
          && gwwResponse.payload.ready
        )
        || gatewayStore.isWorkerLoaded
      ) {
        gatewayStore.setWorkerLoaded(true)
        return true
      } else {
        await trueSleep(WORKER_CONNECTION_INTERVAL)
      }

      attempt++
    }

    tabMetrics.sendException('Worker failed to start/connect from tab!')
    initStore.setState(TabInitStatus.workerBroken)
    return false
  }

  gwBridge.addListener('ready', () => {
    // case when worker loads after tab
    gatewayStore.setWorkerLoaded(true)
  })

  gwBridge.addListener('connectionStatusChanged', (payload) => {
    gatewayStore.setStatus(payload.status)
  })

  gwBridge.addListener('refreshToken', async () => {
    const response = await authStore.refresh()
    return (
      response
        ? { refreshToken: response.refreshToken, accessToken: response.accessToken }
        : { refreshToken: null, accessToken: null }
    )
  })

  gwBridge.addListener('recreateSession', () => {
    authStore.regenerateSessionId()
  })

  gwBridge.addListener('reloadDataRequired', (payload) => {
    // TODO: show notification and/or request new data
    console.warn(`♦️ Data for subscription ${payload.type} outdated! Reload required!`)
  })

  watch(() => initStore.status === TabInitStatus.ready, (isReady) => {
    if (isReady) {
      subscribeStores()
      gwBridge.sendMessage('connect', {
        refreshToken: authStore.refreshToken,
        accessToken: authStore.accessToken,
        sessionId: authStore.sessionId,
      })
    }
  })

  watch(() => authStore.status, (authStatus) => {
    if (authStatus === AuthStatus.notAuthorized) {
      gatewayStore.onLogout()
    }
  })

  watch(() => authStore.sessionId, (sessionId) => {
    gwBridge.sendMessage('updateSession', { sessionId })
  })

  watch(() => authStore.status, (newStatus, oldStatus) => {
    if (newStatus === AuthStatus.authorized) {
      gwBridge.sendMessage('updateAuth', {
        refreshToken: authStore.refreshToken,
        accessToken: authStore.accessToken,
      })
    } else if (oldStatus === AuthStatus.authorized) {
      gwBridge.sendMessage('updateAuth', { refreshToken: null, accessToken: null })
    }
  })

  setInterval(() => {
    gwBridge.sendMessage('ping')
  }, WORKER_PING_INTERVAL)

  return {
    tryConnectWorker,
  }
}
